import { useCallback, useEffect, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useCachedState } from "@/lib/swr";
import { useDash } from "../ctx";
import { PulseDot, PulseWord } from "@/components/pulse/PulseDot";
import { SummaryPanel } from "@/components/pulse/SummaryPanel";
import { HowPulseWorks } from "@/components/pulse/HowPulseWorks";
import { bandOf, movementLine } from "@/components/pulse/pulse";
import { PulseCountsLine } from "./PortfolioPulse";

const COLS = "grid-cols-[24px_1.3fr_200px_110px_120px_110px_40px_100px]";

function Num({ v, sub, zero = false, testid }) {
  return (
    <div className={`text-[13px] text-[#57534E] ${zero ? "opacity-50" : ""}`} data-testid={testid}>
      <b className="text-base font-extrabold text-[#1C1917]">{v}</b>
      {sub && <small className="block text-[11px] text-[#8C827A] leading-tight">{sub}</small>}
    </div>
  );
}

// "Mine → Pulse": every community assigned to this manager. Every number belongs to ONE
// community and opens that community — nothing here is combined.
export default function MinePulse() {
  const { enterCommunity, meUser } = useDash();
  const [data, setData] = useCachedState(`pulse-mine:${meUser?.user_id || ""}`, null);
  const [loading, setLoading] = useState(!data);
  const [openId, setOpenId] = useState(null);
  const [why, setWhy] = useState(null);
  const load = useCallback(() => api.get("/pulse/mine").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false)), [setData]);
  useEffect(() => { load(); }, [load]);
  const rows = data?.communities || [];
  const t = data?.totals || {};
  const whyRows = rows.map((r) => ({ ...r, math: r.tenant_id === why?.tenant_id ? why.math : null }));
  const openWhy = (r) => api.get(`/tenants/${r.tenant_id}/community-pulse`).then((x) => setWhy({ tenant_id: r.tenant_id, math: x.data.math })).catch(() => {});

  return (
    <div className="max-w-[1240px] space-y-5" data-testid="mine-pulse">
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end sm:justify-between gap-3 sm:gap-4">
        <div>
          <PageTitle title="Pulse" about={<>Your {rows.length === 1 ? "community" : `${rows.length} communities`}. Every number belongs to one community — click it to go there.</>} />
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[13px] text-[#57534E]" data-testid="mine-totals">
          <span><b className="text-[15px] text-[#1C1917]">{t.open ?? 0}</b> open requests</span>
          <span><b className="text-[15px] text-[#1C1917]">{t.waiting ?? 0}</b> waiting on you</span>
          <span><b className="text-[15px] text-[#1C1917]">{t.approvals ?? 0}</b> approvals</span>
          <span><b className="text-[15px] text-[#1C1917]">{t.alerts ?? 0}</b> alert{t.alerts === 1 ? "" : "s"}</span>
          <PulseCountsLine counts={data?.counts} />
        </div>
      </div>

      {loading && !data ? (
        <div className="flex justify-center py-24"><div className="h-8 w-8 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>
      ) : (
        <>
        <div className="sm:hidden space-y-3" data-testid="mine-list-mobile">
          {rows.map((r) => {
            const band = bandOf(r);
            const open = openId === r.tenant_id;
            const scored = band !== "off" && band !== "quiet";
            const w = r.waiting || {}, a = r.approvals || {}, rq = r.requests || {}, al = r.alert || {};
            const approvals = (a.reservations || 0) + (a.roster || 0) + (a.invoices || 0) + (a.esign || 0);
            const waiting = w.conversations || 0;
            const flag = al.emergency || al.renewals_overdue > 0;
            return (
              <div key={r.tenant_id} data-testid={`mine-card-${r.tenant_id}`} className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden">
                <button type="button" className="w-full text-left grid grid-cols-[20px_1fr_auto] gap-3 items-center px-4 pt-4 pb-2" onClick={() => enterCommunity(r.tenant_id)}>
                  <PulseDot band={band} quiet={!!r.snapshot?.expected} />
                  <div className="min-w-0">
                    <div className="font-bold text-[15px] text-[#1C1917] truncate">{r.name}</div>
                    <div className="mt-0.5"><PulseWord band={band} /><span className="ml-2 text-xs font-bold text-[#8C827A]">{movementLine(r)}</span></div>
                  </div>
                  {flag && <span className="h-[26px] w-[26px] rounded-lg grid place-items-center text-[13px] font-extrabold bg-[#FEF2F2] text-[#B91C1C] border border-[#F4A3A3]">!</span>}
                </button>
                <div className="flex flex-wrap items-center gap-1.5 px-4 pb-3 text-[12px]">
                  {rq.open != null && <button type="button" onClick={() => enterCommunity(r.tenant_id, "/dashboard/actions")} className={`rounded-full border px-2.5 py-1 ${rq.open ? "border-[#E8E2D9] bg-[#FAF8F5]" : "border-[#F3EFE9] text-[#A8A29E]"}`}><b>{rq.open}</b> requests{rq.urgent ? <span className="text-[#B91C1C] font-bold"> · {rq.urgent} urgent</span> : ""}</button>}
                  <button type="button" onClick={() => enterCommunity(r.tenant_id, "/dashboard/conversations")} className={`rounded-full border px-2.5 py-1 ${waiting ? "border-[#E8E2D9] bg-[#FAF8F5]" : "border-[#F3EFE9] text-[#A8A29E]"}`}><b>{waiting}</b> waiting</button>
                  <button type="button" onClick={() => enterCommunity(r.tenant_id)} className={`rounded-full border px-2.5 py-1 ${approvals ? "border-[#E8E2D9] bg-[#FAF8F5]" : "border-[#F3EFE9] text-[#A8A29E]"}`}><b>{approvals}</b> approvals</button>
                  {scored && (
                    <button type="button" data-testid={`mine-summary-mobile-${r.tenant_id}`} onClick={() => setOpenId(open ? null : r.tenant_id)}
                      className={`ml-auto rounded-full px-3 py-1 text-xs font-bold border ${open ? "bg-ink text-white border-ink" : "bg-white border-[#E8E2D9]"}`}>Summary</button>
                  )}
                </div>
                {open && (
                  <div className="px-3 pb-3">
                    <SummaryPanel tenantId={r.tenant_id} name={r.name} snapshot={r.snapshot} canExpected onWhy={() => openWhy(r)} onOpen={() => enterCommunity(r.tenant_id)} onExpectedChange={load} />
                  </div>
                )}
              </div>
            );
          })}
          {rows.length === 0 && <div className="bg-white rounded-2xl border border-[#E8E2D9] p-8 text-center text-sm text-[#78716C]">No communities are assigned to you yet.</div>}
        </div>
        <div className="hidden sm:block bg-white rounded-2xl border border-[#E8E2D9] overflow-x-auto" data-testid="mine-list">
          <div className="min-w-[900px]">
            <div className={`grid ${COLS} gap-3.5 px-5 py-2.5 border-b border-[#E8E2D9] bg-[#FAF8F5] text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#8C827A]`}>
              <span /><span>Community</span><span>Pulse</span><span>Requests</span><span>Waiting on you</span><span>Approvals</span><span /><span />
            </div>
            {rows.map((r) => {
              const band = bandOf(r);
              const open = openId === r.tenant_id;
              const scored = band !== "off" && band !== "quiet";
              const w = r.waiting || {}, a = r.approvals || {}, rq = r.requests || {}, al = r.alert || {};
              const approvals = (a.reservations || 0) + (a.roster || 0) + (a.invoices || 0) + (a.esign || 0);
              const appSub = [a.reservations && `${a.reservations} reservations`, a.roster && `${a.roster} roster`, a.invoices && `${a.invoices} invoices`, a.esign && `${a.esign} e-sign`].filter(Boolean).join(" · ");
              const flag = al.emergency || al.renewals_overdue > 0;
              return (
                <div key={r.tenant_id} data-testid={`mine-row-${r.tenant_id}`}
                  className={`grid ${COLS} gap-3.5 items-center px-5 py-3.5 border-b border-[#F3EFE9] last:border-0 cursor-pointer hover:bg-[#FAF8F5] ${open ? "bg-[#FAF8F5]" : ""}`}
                  onClick={() => enterCommunity(r.tenant_id)}>
                  <PulseDot band={band} quiet={!!r.snapshot?.expected} testid={`mine-dot-${r.tenant_id}`} />
                  <div className="min-w-0">
                    <div className="font-bold text-[15px] text-[#1C1917] truncate">{r.name}</div>
                    <div className="text-xs text-[#8C827A] mt-0.5">{r.homes ? `${r.homes} properties` : ""}</div>
                  </div>
                  <div className="min-w-0">
                    <PulseWord band={band} testid={`mine-word-${r.tenant_id}`} />
                    <div className="text-xs font-bold text-[#8C827A] mt-0.5 truncate">{movementLine(r)}</div>
                  </div>
                  {rq.open == null ? <Num v="—" sub="module off" zero /> : <Num v={rq.open} sub={rq.urgent ? <span className="text-[#B91C1C] font-bold">{rq.urgent} urgent</span> : "open"} zero={!rq.open} testid={`mine-requests-${r.tenant_id}`} />}
                  <Num v={w.conversations || 0} sub={w.conversations ? `conversation${w.conversations === 1 ? "" : "s"}` : ""} zero={!w.conversations} testid={`mine-waiting-${r.tenant_id}`} />
                  <Num v={approvals} sub={appSub} zero={!approvals} testid={`mine-approvals-${r.tenant_id}`} />
                  <span data-testid={`mine-flag-${r.tenant_id}`} title={flag ? (al.emergency ? "Emergency alert open" : `${al.renewals_overdue} renewal${al.renewals_overdue === 1 ? "" : "s"} overdue`) : ""}
                    className={`h-[26px] w-[26px] rounded-lg grid place-items-center text-[13px] font-extrabold ${flag ? "bg-[#FEF2F2] text-[#B91C1C] border border-[#F4A3A3]" : ""}`}>{flag ? "!" : ""}</span>
                  <div className="justify-self-end" onClick={(e) => e.stopPropagation()}>
                    {scored && (
                      <button type="button" data-testid={`mine-summary-btn-${r.tenant_id}`} onClick={() => setOpenId(open ? null : r.tenant_id)}
                        className={`rounded-full px-3 py-1.5 text-xs font-bold border ${open ? "bg-ink text-white border-ink" : "bg-white border-[#E8E2D9] hover:border-ink"}`}>Summary</button>
                    )}
                  </div>
                  {open && (
                    <div className="col-span-full" onClick={(e) => e.stopPropagation()}>
                      <SummaryPanel tenantId={r.tenant_id} name={r.name} snapshot={r.snapshot} canExpected onWhy={() => openWhy(r)}
                        onOpen={() => enterCommunity(r.tenant_id)} onExpectedChange={load} />
                    </div>
                  )}
                </div>
              );
            })}
            {rows.length === 0 && <div className="p-10 text-center text-sm text-[#78716C]" data-testid="mine-empty">No communities are assigned to you yet.</div>}
          </div>
        </div>
        </>
      )}
      <div className="flex flex-wrap justify-between gap-2 text-xs text-[#8C827A]">
        <span>Only communities assigned to you. Nothing here is combined — each count is one community's, and opens that community's page.</span>
        <span>! = active emergency alert or overdue renewal</span>
      </div>
      <HowPulseWorks open={!!why} onOpenChange={(o) => !o && setWhy(null)} rows={whyRows} selectedId={why?.tenant_id} canTune={false} mode={null} />
    </div>
  );
}
